/**
 * Pure assembly of the "Report chat trace" support bundle.
 *
 * The report dialog collects live debug state (thread/runs), the conversation
 * text, and the user's free-form feedback, then copies the whole thing to the
 * clipboard as one JSON document that can be pasted straight to the support
 * team. Everything serializable lives here so the shape stays stable and
 * testable without Angular.
 */

/** One observed chat run, newest-first as stored by DebugStreamService. */
export interface ChatRunTraceEntry {
  run_id: string;
  thread_id: string;
  status: "running" | "success" | "error";
  /** ISO timestamp captured when the RUN_STARTED event arrived. */
  started_at: string;
}

/** A conversation entry in the report — plain text only, by design. */
export interface ChatTraceReportMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatTraceReport {
  schema: "chat-trace-report/v1";
  generated_at: string;
  user: string | null;
  agent_id: string | null;
  thread_id: string | null;
  /** Newest run observed for the thread, null when no run was seen this session. */
  current_run_id: string | null;
  /** Runs observed for the current thread (or all runs when the thread is unknown), newest-first. */
  runs: ChatRunTraceEntry[];
  /** First user text message of the conversation, null when the user has not written yet. */
  initial_input: string | null;
  /** Plain-text user/assistant messages in chronological order. */
  conversation: ChatTraceReportMessage[];
  user_feedback: string;
}

export interface ChatTraceReportInput {
  generatedAt: Date;
  user: string | null;
  agentId: string | null;
  threadId: string | null;
  /** Session run registry (newest-first), unfiltered — the builder scopes it to the thread. */
  runs: ChatRunTraceEntry[];
  /** Raw agent store messages (AG-UI shape: { id, role, content }); filtered and flattened here. */
  rawMessages: unknown[];
  feedback: string;
}

/**
 * True when the raw message contributes text to the report conversation: only
 * user/assistant roles with plain string content. Tool/system/developer roles
 * and block-array content (content blocks) are deliberately excluded — the
 * report carries readable chat text, not structured stream payloads.
 */
function isReportableTextMessage(raw: unknown): raw is { role: string; content: string } {
  const m = raw as { role?: unknown; content?: unknown } | null;
  if (m == null || typeof m !== "object") return false;
  if (m.role !== "user" && m.role !== "assistant") return false;
  return typeof m.content === "string" && m.content.trim().length > 0;
}

/**
 * Builds the clipboard-ready support report from live chat state.
 * Pure function — no Angular, no services; the dialog feeds it signals.
 */
export function buildChatTraceReport(input: ChatTraceReportInput): ChatTraceReport {
  const conversation: ChatTraceReportMessage[] = [];
  let initialInput: string | null = null;
  for (const raw of input.rawMessages) {
    if (!isReportableTextMessage(raw)) continue;
    const content = raw.content.trim();
    const role = raw.role as "user" | "assistant";
    conversation.push({ role, content });
    if (role === "user" && initialInput === null) initialInput = content;
  }

  // The report describes the current thread; when the thread is known, drop
  // runs that belong to other threads (agent switches keep the session registry).
  const runs = input.threadId === null ? input.runs : input.runs.filter((r) => r.thread_id === input.threadId);

  return {
    schema: "chat-trace-report/v1",
    generated_at: input.generatedAt.toISOString(),
    user: input.user,
    agent_id: input.agentId,
    thread_id: input.threadId,
    current_run_id: runs.length > 0 ? runs[0].run_id : null,
    runs,
    initial_input: initialInput,
    conversation,
    user_feedback: input.feedback,
  };
}
